import React, { Suspense, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import { apiClient } from '../../services/api/apiService';
import {
  ChartCard, ChartsFallback, CumulativeWinsChart, HeadToHeadChart, PointsPerGameChart, ResultsByNightChart,
} from './charts';
import { formatNight, ordinal, recordLabel } from './leagueStats';
import ClaimInvitesPanel from './ClaimInvitesPanel';
import './leagues.css';
import mediaUrl from "../../utils/mediaUrl";

const playerName = (p) => [p.first_name, p.last_name].filter(Boolean).join(' ') || p.username || 'Player';

/** "1-0 (21-17)" for set sports, "70-64" otherwise. */
const scoreLine = (game, usesSets) => {
  const points = `${game.points_for}-${game.points_against}`;
  if (usesSets && game.sets_for != null) {
    const sets = `${game.sets_for}-${game.sets_against}`;
    return game.points_for || game.points_against ? `${sets} (${points})` : sets;
  }
  return points;
};

const Stat = ({ label, value }) => (
  <div className="league-team-stat">
    <span className="league-team-stat__value">{value}</span>
    <span className="league-team-stat__label">{label}</span>
  </div>
);

/**
 * /leagues/:id/teams/:teamId: one team (or player) inside the league: rank,
 * record, charts, recent games and roster. Back returns to the Standings tab.
 */
const LeagueTeamPage = ({ token }) => {
  const { id, teamId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, error: null });
    apiClient.get(`/leagues/${id}/teams/${teamId}/stats`)
      .then((res) => alive && setState({ loading: false, data: res.data?.data || null, error: null }))
      .catch((err) => alive && setState({
        loading: false,
        data: null,
        error: [403, 404].includes(err.response?.status)
          ? 'Team not found in this league.'
          : err.response?.data?.message || 'Could not load the team.',
      }));
    return () => { alive = false; };
  }, [id, teamId]);

  const back = () => navigate(`/leagues/${id}?tab=standings`);
  const { loading, data, error } = state;
  const team = data?.team;
  const usesSets = Boolean(data?.uses_sets);

  return (
    <GameLayout token={token} onLogout={() => {}}>
      <div className="league-team-page">
        <button type="button" className="league-team-page__back" onClick={back}>
          ← Back to standings
        </button>

        {loading && <p className="league-team-page__note">Loading…</p>}
        {!loading && error && <p className="league-team-page__note" role="alert">{error}</p>}

        {team && (
          <>
            <header className="league-team-head">
              {team.logo_url && <img className="league-team-head__logo" src={mediaUrl(team.logo_url)} alt="" />}
              <div className="league-team-head__titles">
                <h1 className="league-team-head__name">{team.team_name}</h1>
                <p className="league-team-head__league">{data.league?.name}</p>
              </div>
            </header>

            <div className="league-team-stats" aria-label="Season numbers">
              <Stat label="Rank" value={team.games_played ? `${ordinal(team.position)} of ${team.entries}` : '—'} />
              <Stat label="Record" value={recordLabel(team)} />
              <Stat label="Win %" value={`${Number(team.win_percentage || 0).toFixed(1)}%`} />
              <Stat label="Points for / against" value={`${team.points_for} / ${team.points_against}`} />
              <Stat label="Streak" value={team.streak || '—'} />
            </div>

            {team.games_played === 0 ? (
              <p className="league-team-page__empty">No results yet</p>
            ) : (
              <>
                <Suspense fallback={<ChartsFallback />}>
                  <div className="league-charts-grid">
                    <ChartCard title="Results by night">
                      <ResultsByNightChart byNight={data.by_night} />
                    </ChartCard>
                    <ChartCard title="Wins over the season vs league average">
                      <CumulativeWinsChart byNight={data.by_night} teamName={team.team_name} />
                    </ChartCard>
                    <ChartCard title="Points per game (running average)">
                      <PointsPerGameChart perGame={data.per_game} />
                    </ChartCard>
                    <ChartCard title="Record against each opponent">
                      <HeadToHeadChart rows={data.head_to_head} />
                    </ChartCard>
                  </div>
                </Suspense>

                <section className="league-team-section" aria-label="Recent games">
                  <h2 className="league-team-section__title">Recent games</h2>
                  <ul className="league-recent">
                    {data.recent.map((game, i) => {
                      const target = game.match_short_code || game.match_id;
                      const content = (
                        <>
                          <span className={`league-recent__result league-recent__result--${game.result}`}>{game.result}</span>
                          <span className="league-recent__score">{scoreLine(game, usesSets)}</span>
                          <span className="league-recent__opponent">vs {game.opponent_name}</span>
                          <span className="league-recent__night">{formatNight(game.night)}</span>
                        </>
                      );
                      return (
                        <li key={`${game.match_id || 'g'}-${i}`}>
                          {target ? (
                            <button type="button" className="league-recent__row" onClick={() => navigate(`/matches/${target}`)}>
                              {content}
                            </button>
                          ) : (
                            <div className="league-recent__row">{content}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              </>
            )}

            <section className="league-team-section" aria-label="Roster">
              <h2 className="league-team-section__title">Roster</h2>
              {data.roster?.length ? (
                <ul className="league-roster">
                  {data.roster.map((p) => (
                    <li key={p.user_id} className="league-roster__player">
                      <span>{playerName(p)}</span>
                      {p.is_captain && team.participant_type !== 'user' && (
                        <span className="league-roster__captain" title="Captain">C</span>
                      )}
                      {p.pending && <span className="league-roster__pending" title="Hasn't joined Sportifide yet">not joined</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="league-team-page__note">No active players.</p>
              )}
            </section>

            {team.team_id && <ClaimInvitesPanel teamId={team.team_id} />}
          </>
        )}
      </div>
    </GameLayout>
  );
};

export default LeagueTeamPage;
